import React, { useState, useEffect, useCallback, useRef } from 'react';
import styled from 'styled-components';
import { type TranscriptCue, srtTimecodeToSeconds, secondsToSrtTimecode } from '@bynder-studio/render-core';
import { Button, Input, Textarea, textStyles, token } from '@bynder/design-system';
import { IconAdd, IconControlRightSymbol, IconDeleteOutline } from '@bynder/icons';
import { useTranslate } from '@bynder/localization';
import useOnClickOutside from 'packages/hooks/useOnClickOutside';
import { formatShortTimecode, parseShortTimecode } from './parseSrt';

const SHORT_TIMECODE_REGEX = /^\d{2}:\d{2}(,\d{1,3})?$/;

type TranscriptCueItemProps = {
    cue: TranscriptCue;
    index: number;
    isEditing: boolean;
    isLast: boolean;
    onStartEdit: (id: string) => void;
    onEndEdit: (id: string) => void;
    onUpdate: (updatedCue: TranscriptCue) => void;
    onDelete: (id: string) => void;
    onInsertBefore: (id: string) => void;
    onInsertAfter: (id: string) => void;
    onSeek?: (cue: TranscriptCue) => void;
};

const TranscriptCueItem = ({
    cue,
    index,
    isEditing,
    isLast,
    onStartEdit,
    onEndEdit,
    onUpdate,
    onDelete,
    onInsertBefore,
    onInsertAfter,
    onSeek,
}: TranscriptCueItemProps) => {
    const { translate } = useTranslate();
    const [editStart, setEditStart] = useState('');
    const [editEnd, setEditEnd] = useState('');
    const [localText, setLocalText] = useState(cue.text);
    const isTextFocusedRef = useRef(false);
    const cueContainerRef = useRef<HTMLDivElement>(null);
    const timestampRowRef = useRef<HTMLDivElement>(null);

    const shortStart = formatShortTimecode(srtTimecodeToSeconds(cue.startTime));

    // Sync local text when an external update changes cue.text (e.g. generate)
    useEffect(() => {
        if (!isTextFocusedRef.current) {
            setLocalText(cue.text);
        }
    }, [cue.text]);

    // Populate timestamp inputs when entering edit mode
    useEffect(() => {
        if (!isEditing) {
            return;
        }

        setEditStart(formatShortTimecode(srtTimecodeToSeconds(cue.startTime), true));
        setEditEnd(formatShortTimecode(srtTimecodeToSeconds(cue.endTime), true));
    }, [isEditing, cue.startTime, cue.endTime]);

    const enterEditMode = useCallback(() => {
        onStartEdit(cue.id);
    }, [cue.id, onStartEdit]);

    // Jump the video playhead to this cue's start frame when its timestamp is clicked/focused.
    const handleSeek = useCallback(() => {
        onSeek?.(cue);
    }, [cue, onSeek]);

    const commitTimestamps = useCallback(() => {
        const startSecs = SHORT_TIMECODE_REGEX.test(editStart)
            ? parseShortTimecode(editStart)
            : srtTimecodeToSeconds(cue.startTime);
        const endSecs = SHORT_TIMECODE_REGEX.test(editEnd)
            ? parseShortTimecode(editEnd)
            : srtTimecodeToSeconds(cue.endTime);
        // Preserve exactly what the user typed (to millisecond precision) by storing
        // canonical SRT timecodes — frames are derived only at render time.
        const startTime = secondsToSrtTimecode(Math.max(0, startSecs));
        const endTime = secondsToSrtTimecode(Math.max(startSecs, endSecs));

        onUpdate({
            ...cue,
            startTime,
            endTime,
        });
        onEndEdit(cue.id);
    }, [editStart, editEnd, cue, onUpdate, onEndEdit]);

    // Live-update the cue (and therefore the canvas) on every keystroke, mirroring TextElement.
    // Untrimmed while typing so trailing spaces are preserved; trimming happens on blur.
    const handleTextChange = useCallback(
        (value: string) => {
            setLocalText(value);

            if (value !== cue.text) {
                onUpdate({ ...cue, text: value });
            }
        },
        [cue, onUpdate],
    );

    const commitText = useCallback(() => {
        const trimmed = localText.trim();

        if (trimmed !== cue.text) {
            onUpdate({ ...cue, text: trimmed });
        }
    }, [localText, cue, onUpdate]);

    const handleTimestampRowBlur = useCallback(
        (e: React.FocusEvent<HTMLDivElement>) => {
            if (!cueContainerRef.current?.contains(e.relatedTarget as Node)) {
                commitTimestamps();
            }
        },
        [commitTimestamps],
    );

    useOnClickOutside(cueContainerRef, () => {
        if (!isEditing) {
            return;
        }

        const rowNode = cueContainerRef.current;
        const activeElement = document.activeElement;

        // If one of timestamp inputs is focused, blur handler will commit.
        if (rowNode && activeElement && rowNode.contains(activeElement)) {
            return;
        }

        commitTimestamps();
    });

    const handleDelete = useCallback(() => {
        onDelete(cue.id);
    }, [cue.id, onDelete]);

    const handleInsertAfter = useCallback(() => {
        onInsertAfter(cue.id);
    }, [cue.id, onInsertAfter]);

    const handleInsertBefore = useCallback(() => {
        onInsertBefore(cue.id);
    }, [cue.id, onInsertBefore]);

    const handleTextareaKeyDown = useCallback((e: React.KeyboardEvent) => {
        // Keep caret navigation inside textarea from triggering parent shortcuts.
        if (e.key.startsWith('Arrow')) {
            e.stopPropagation();
        }
    }, []);

    return (
        <CueContainer ref={cueContainerRef}>
            {isEditing ? (
                <div ref={timestampRowRef} onBlur={handleTimestampRowBlur}>
                    <CueIndexRow>
                        <CueIndexLabel>{index}</CueIndexLabel>
                        <AddCueSlot onDoubleClick={(e) => e.stopPropagation()}>
                            <HeaderAddButton
                                variant="clean"
                                isSmall
                                icon={<IconAdd />}
                                onClick={handleInsertBefore}
                                aria-label={translate('editor.transcript.cue.add-before')}
                                title={translate('editor.transcript.cue.add-before')}
                            />
                        </AddCueSlot>
                    </CueIndexRow>
                    <TimestampEditRow>
                        <Input
                            isSmall
                            value={editStart}
                            onChange={setEditStart}
                            onFocus={handleSeek}
                            aria-label={translate('editor.transcript.cue.start-time')}
                        />
                        <SeparatorWrapper>
                            <IconControlRightSymbol />
                        </SeparatorWrapper>
                        <Input
                            isSmall
                            value={editEnd}
                            onChange={setEditEnd}
                            aria-label={translate('editor.transcript.cue.end-time')}
                        />
                        <Button
                            variant="clean"
                            isSmall
                            icon={<IconDeleteOutline />}
                            onClick={handleDelete}
                            aria-label={translate('editor.transcript.cue.delete')}
                            title={translate('editor.transcript.cue.delete')}
                        />
                    </TimestampEditRow>
                </div>
            ) : (
                <CueHeader onDoubleClick={enterEditMode}>
                    <CueHeaderMeta
                        onClick={handleSeek}
                        role="button"
                        tabIndex={0}
                        title={translate('editor.transcript.cue.seek')}
                    >
                        {index} | {shortStart}
                    </CueHeaderMeta>
                    <AddCueSlot onDoubleClick={(e) => e.stopPropagation()}>
                        <HeaderAddButton
                            variant="clean"
                            isSmall
                            icon={<IconAdd />}
                            onClick={handleInsertBefore}
                            aria-label={translate('editor.transcript.cue.add-before')}
                            title={translate('editor.transcript.cue.add-before')}
                        />
                    </AddCueSlot>
                </CueHeader>
            )}
            <StyledTextarea
                value={localText}
                onChange={handleTextChange}
                onKeyDown={handleTextareaKeyDown}
                onFocus={() => {
                    isTextFocusedRef.current = true;
                    handleSeek();
                }}
                onBlur={() => {
                    isTextFocusedRef.current = false;
                    commitText();
                }}
                aria-label={translate('editor.transcript.cue.text')}
            />
            {isLast && (
                <BottomHoverZone>
                    <AddCueButton
                        variant="clean"
                        isSmall
                        icon={<IconAdd />}
                        onClick={handleInsertAfter}
                        aria-label={translate('editor.transcript.cue.add-after')}
                        title={translate('editor.transcript.cue.add-after')}
                    />
                </BottomHoverZone>
            )}
        </CueContainer>
    );
};

const CueContainer = styled.div`
    position: relative;
    padding: ${token.spacing3};
    border-radius: 10px;
`;

const CueHeader = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: ${token.spacing2};
    margin-bottom: ${token.spacing2};
    cursor: pointer;
    user-select: none;
`;

const CueIndexRow = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: ${token.spacing2};
`;

const CueIndexLabel = styled.span`
    ${textStyles.uiTextXS};
    color: ${token.gray500a};
`;

const CueHeaderMeta = styled.span`
    ${textStyles.uiTextXS};
    color: ${token.colorTextDisabled};
`;

const TimestampEditRow = styled.div`
    display: grid;
    margin-bottom: ${token.spacing2};
    gap: ${token.spacing2};
    grid-template-columns: 110px 26px 110px 40px;
    align-items: center;
`;
const SeparatorWrapper = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
`;

const StyledTextarea = styled(Textarea)`
    min-height: 72px;
`;

const AddCueButton = styled(Button)`
    position: absolute;
    bottom: calc(50% - 10px);
    transform: translateY(50%);
    right: 0;
    z-index: 1;
    opacity: 0;
`;

const HeaderAddButton = styled(Button)`
    opacity: 0;
`;

const AddCueSlot = styled.div`
    display: flex;
    align-items: center;
    padding-left: ${token.spacing4};

    &:hover ${HeaderAddButton}, &:focus-within ${HeaderAddButton} {
        opacity: 1;
    }
`;

const BottomHoverZone = styled.div`
    position: relative;
    height: 16px;
    margin-top: ${token.spacing1};

    &:hover ${AddCueButton}, &:focus-within ${AddCueButton} {
        opacity: 1;
    }
`;

export default React.memo(TranscriptCueItem);
